import {
  fetchSinoaInsights,
  fetchKeywordRelationships,
  fetchKeywordIntelligence,
  fetchKeywordStats,
  fetchRelationshipExplanation,
} from '@/api/sinoaInsights';

const defaultInsights = () => ({
  daily_questions_count: 0,
  questions_count_delta: null,
  top_topic: { topic: 'N/A', percentage: 0 },
  market_sentiment: 'neutral',
  trending_questions: [],
  market_focus: { selling_intent: 0, risk_concerns: 0, crop_outlook: 0 },
  market_focus_deltas: { selling_intent: null, risk_concerns: null, crop_outlook: null },
  word_cloud: [],
  recent_alerts: [],
});

const defaultTrendDeepDive = () => ({
  visible: false,
  keyword: '',
  loading: false,
  error: null,
  found: false,
  relationships: [],
  intelligence: { loading: false, error: null, data: null },
  stats: { loading: false, percentage: null, delta: null },
  selectedRelEntity: null,
  relExplanation: { loading: false, text: null },
});

const SENTIMENT_CONFIG = {
  optimistic: { label: 'Optimistic', color: 'green--text' },
  neutral: { label: 'Neutral', color: 'grey--text text--darken-2' },
  cautious: { label: 'Cautious', color: 'orange--text text--darken-2' },
  pessimistic: { label: 'Pessimistic', color: 'red--text' },
};

const DURATION_LABELS = {
  30: 'Last 30 days',
  60: 'Last 60 days',
  90: 'Last 90 days',
  365: 'Last year',
};

export default {
  namespaced: true,

  state: () => ({
    loading: false,
    error: null,
    selectedDays: 30,
    insights: defaultInsights(),
    trendDeepDive: defaultTrendDeepDive(),
  }),

  getters: {
    sentimentLabel: (state) => SENTIMENT_CONFIG[state.insights.market_sentiment]?.label || 'Neutral',

    sentimentColor: (state) => SENTIMENT_CONFIG[state.insights.market_sentiment]?.color || 'grey--text',

    durationLabel: (state) => DURATION_LABELS[state.selectedDays] || 'Last 30 days',

    deltaIcon: (state) => (state.insights.questions_count_delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'),

    deltaIconColor: (state) => (state.insights.questions_count_delta >= 0 ? 'green' : 'red'),

    deltaColor: (state) => (state.insights.questions_count_delta >= 0 ? 'green--text' : 'red--text'),

    formattedDelta: (state) => Math.abs(state.insights.questions_count_delta),

    gaugeConfigs: (state) => {
      const enrichDelta = (g) => ({
        ...g,
        deltaClass: g.delta >= 0 ? 'green--text' : 'red--text',
        deltaIconColor: g.delta >= 0 ? 'green' : 'red',
        deltaIcon: g.delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down',
        formattedDelta: Math.abs(g.delta),
      });
      return [
        enrichDelta({ ref: 'gaugeSellingIntent', label: 'Selling Intent', value: state.insights.market_focus.selling_intent, delta: state.insights.market_focus_deltas.selling_intent }),
        enrichDelta({ ref: 'gaugeRiskConcerns', label: 'Risk Concerns', value: state.insights.market_focus.risk_concerns, delta: state.insights.market_focus_deltas.risk_concerns }),
        enrichDelta({ ref: 'gaugeCropOutlook', label: 'Crop Outlook', value: state.insights.market_focus.crop_outlook, delta: state.insights.market_focus_deltas.crop_outlook }),
      ];
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, value) {
      state.error = value;
    },

    setSelectedDays(state, days) {
      state.selectedDays = days;
    },

    setInsights(state, insights) {
      state.insights = insights;
    },

    setTrendDeepDive(state, updates) {
      state.trendDeepDive = { ...state.trendDeepDive, ...updates };
    },

    setTrendDeepDiveIntelligence(state, updates) {
      state.trendDeepDive = {
        ...state.trendDeepDive,
        intelligence: { ...state.trendDeepDive.intelligence, ...updates },
      };
    },

    setTrendDeepDiveStats(state, updates) {
      state.trendDeepDive = {
        ...state.trendDeepDive,
        stats: { ...state.trendDeepDive.stats, ...updates },
      };
    },

    setTrendDeepDiveRelExplanation(state, updates) {
      state.trendDeepDive = {
        ...state.trendDeepDive,
        relExplanation: { ...state.trendDeepDive.relExplanation, ...updates },
      };
    },
  },

  actions: {
    async loadInsights({ commit, state }) {
      commit('setLoading', true);
      commit('setError', null);
      try {
        const data = await fetchSinoaInsights(state.selectedDays);
        commit('setInsights', data);
      } catch {
        commit('setError', 'Failed to load Sinoa insights. Please try again later.');
      } finally {
        commit('setLoading', false);
      }
    },

    setSelectedDays({ commit, dispatch }, days) {
      commit('setSelectedDays', days);
      dispatch('loadInsights');
    },

    async loadKeywordData({ commit, state }, keyword) {
      commit('setTrendDeepDive', {
        visible: true,
        keyword,
        loading: true,
        error: null,
        found: false,
        relationships: [],
        selectedRelEntity: null,
        relExplanation: { loading: false, text: null },
      });
      commit('setTrendDeepDiveIntelligence', { loading: true, error: null, data: null });
      commit('setTrendDeepDiveStats', { loading: true, percentage: null, delta: null });

      // Fire intelligence and stats in parallel — do not block the caller
      fetchKeywordIntelligence(keyword)
        .then((data) => commit('setTrendDeepDiveIntelligence', { loading: false, error: null, data }))
        .catch(() => commit('setTrendDeepDiveIntelligence', {
          loading: false, data: null, error: 'Failed to load keyword intelligence.',
        }));

      fetchKeywordStats(keyword, state.selectedDays)
        .then((data) => commit('setTrendDeepDiveStats', {
          loading: false, percentage: data.percentage, delta: data.delta,
        }))
        .catch(() => commit('setTrendDeepDiveStats', { loading: false, percentage: null, delta: null }));

      // Return relationships so the component can trigger chart rendering
      try {
        const data = await fetchKeywordRelationships(keyword);
        commit('setTrendDeepDive', {
          found: data.found,
          relationships: data.relationships || [],
          loading: false,
        });
        return data;
      } catch {
        commit('setTrendDeepDive', {
          error: 'Failed to load keyword relationships.',
          loading: false,
        });
        return null;
      }
    },

    async loadRelExplanation({ commit, state }, entityName) {
      commit('setTrendDeepDive', { selectedRelEntity: entityName });
      commit('setTrendDeepDiveRelExplanation', { loading: true, text: null });
      try {
        const data = await fetchRelationshipExplanation(state.trendDeepDive.keyword, entityName);
        commit('setTrendDeepDiveRelExplanation', { loading: false, text: data.explanation });
      } catch {
        commit('setTrendDeepDiveRelExplanation', {
          loading: false,
          text: 'Failed to load explanation.',
        });
      }
    },
  },
};
